import { useTranslation } from 'react-i18next'
import type { ReactElement } from 'react'
import { useEffect, useRef } from 'react'
import { Box, Tooltip } from '@chakra-ui/react'
import { IKReactSelect } from '../../../../../ui-kit'
import type { CmFieldResponseObject } from '../../../../interfaces'

const BulkEditModeDropdown = ({
  cmField,
  bulkEditFieldMode,
  disabled,
  setBulkEditFieldMode,
  onInputChange,
  menuPosition,
}: BulkEditModeDropdownProps): ReactElement => {
  const { t } = useTranslation()
  const isRequiredField = cmField.schema.isValueRequired
  const alterationOptions = [
    { label: t('set_value', 'Set value'), value: 'set' },
  ]
  if (!isRequiredField) {
    alterationOptions.unshift({
      label: t('clear_field', 'Clear field'),
      value: 'clear',
    })
  }

  useEffect(() => {
    // Reset to 'set' if field is required
    if (
      cmField.schema.isValueRequired &&
      bulkEditFieldMode[cmField.name] === 'clear'
    ) {
      setBulkEditFieldMode({ ...bulkEditFieldMode, [cmField.name]: 'set' })
    }
  }, [cmField, bulkEditFieldMode, setBulkEditFieldMode])

  return (
    <Tooltip
      label={t(
        'required_field_cannot_be_cleared',
        'Required field cannot be cleared',
      )}
      isDisabled={!isRequiredField}
      hasArrow
    >
      <Box
        mb={bulkEditFieldMode[cmField.name] === 'clear' ? 0 : '1em'}
        w="100%"
        data-testid="bulk-edit-mode-dropdown"
      >
        <IKReactSelect
          placeholder={t('select_action', 'Select action')}
          value={
            alterationOptions.find(
              option => option.value === bulkEditFieldMode[cmField.name],
            ) ?? null
          }
          options={alterationOptions}
          variant="compact"
          captureMenuScroll={false}
          menuPlacement="auto"
          menuShouldScrollIntoView={true}
          menuPosition={menuPosition}
          onChange={newVal => {
            const currentValue = bulkEditFieldMode[cmField.name]
            const newValue = newVal ? (newVal.value as 'set' | 'clear') : 'set'
            setBulkEditFieldMode({
              ...bulkEditFieldMode,
              [cmField.name]: newValue,
            })
            if (currentValue !== newValue) {
              onInputChange?.()
            }
          }}
          isDisabled={isRequiredField || disabled}
          isSearchable={false}
        />
      </Box>
    </Tooltip>
  )
}

export default BulkEditModeDropdown

export interface BulkEditFieldObject {
  [key: string]: 'set' | 'clear'
}

export interface BulkEditModeDropdownProps {
  cmField: CmFieldResponseObject
  bulkEditFieldMode: BulkEditFieldObject
  disabled?: boolean
  menuPosition?: 'absolute' | 'fixed'
  setBulkEditFieldMode: React.Dispatch<
    React.SetStateAction<BulkEditFieldObject>
  >
  onInputChange?(): void
}
